// pages/expenses/ExpenseCategoryList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import Table from '../../core/pagination/datatable';
import {
    ChevronUp,

    Search,
    Eye,
    Edit,
    Trash2,
    PlusCircle,
    Folder,
    RefreshCw,

    Briefcase,
    Home,
    Zap,
    Users,
    MapPin,
    TrendingUp,
    Tool,
    Circle,
    AlertCircle
} from 'feather-icons-react/build/IconComponents';
import { expenseCategoryActions } from '../../core/action/expenseCategoryActions';
import AddExpenseCategory from '../../core/modals/expenses/AddExpenseCategory';
import EditExpenseCategory from '../../core/modals/expenses/EditExpenseCategory';
import ViewExpenseCategory from '../../core/modals/expenses/ViewExpenseCategory';

const MySwal = withReactContent(Swal);

const ExpenseCategoryList = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    //const [isFilterVisible, setIsFilterVisible] = useState(false);

    // Modal states
    const [modalType, setModalType] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        status: ''
    });

    // Sort options
    const sortOptions = useMemo(() => [
        { value: 'category_name', label: 'Category Name' },
        { value: 'status', label: 'Status' },
        { value: 'created_at', label: 'Created Date' }
    ], []);

    const [sortOption, setSortOption] = useState('created_at');

    // Status options
    const statusOptions = useMemo(() => [
        { value: '', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
    ], []);

    // Load categories
    const loadCategories = useCallback(async (page = pagination.page, overrideFilters = null, overrideSortOption = null) => {
        try {
            setLoading(true);

            const effectiveFilters = overrideFilters !== null ? overrideFilters : filters;
            const effectiveSortOption = overrideSortOption !== null ? overrideSortOption : sortOption;

            const params = {
                page,
                limit: pagination.limit,
                search: effectiveFilters.search || undefined,
                status: effectiveFilters.status || undefined,
                sort_by: effectiveSortOption
            };

            const result = await expenseCategoryActions.getCategories(params);

            console.log('Categories API response:', result);

            if (result.success) {
                setCategories(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load categories',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [pagination.limit, filters, sortOption]);

    // Load initial data
    useEffect(() => {
        loadCategories();
    }, []);

    // Handle search
    const handleSearch = useCallback(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadCategories(1);
    }, [loadCategories]);

    // Toggle filter visibility
    // const toggleFilterVisibility = useCallback(() => {
    //     setIsFilterVisible((prev) => !prev);
    // }, []);

    // Reset filters
    const resetFilters = useCallback(() => {
        const emptyFilters = {
            search: '',
            status: ''
        };
        setFilters(emptyFilters);
        setSortOption('category_name');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadCategories(1, emptyFilters, 'category_name');
    }, [loadCategories]);

    // Handle filter change
    const handleFilterChange = useCallback((filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    }, []);

    // Handle sort change
    const handleSortChange = useCallback((option) => {
        setSortOption(option?.value || 'category_name');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadCategories(1);
    }, [loadCategories]);

    // Handle page change
    const handlePageChange = useCallback((page) => {
        setPagination(prev => ({ ...prev, page }));
        loadCategories(page);
    }, [loadCategories]);

    // Modal handlers
    const handleAdd = useCallback(() => {
        setModalType('add');
        setSelectedCategory(null);
    }, []);

    const handleView = useCallback((category) => {
        setSelectedCategory(category);
        setModalType('view');
    }, []);

    const handleEdit = useCallback((category) => {
        setSelectedCategory(category);
        setModalType('edit');
    }, []);

    const handleModalClose = useCallback(() => {
        setModalType(null);
        setSelectedCategory(null);
    }, []);

    const handleSuccess = useCallback(() => {
        loadCategories(pagination.page);
        handleModalClose();
    }, [loadCategories, pagination.page, handleModalClose]);

    // Handle delete
    const handleDelete = useCallback(async (category) => {
        // Check if default category
        if (category.is_default === 1) {
            MySwal.fire({
                icon: 'warning',
                title: 'Cannot Delete',
                text: 'Default categories cannot be deleted.',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        const result = await MySwal.fire({
            title: 'Delete Category?',
            html: `
                <div class="text-start">
                    <p>Are you sure you want to delete this category?</p>
                    <p><strong>Category:</strong> ${category.category_name}</p>
                    <p class="text-danger">This action cannot be undone.</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const categoryId = category.id || category.category_id || category.expense_category_id;
                const deleteResult = await expenseCategoryActions.deleteCategory(categoryId);

                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Category has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    loadCategories(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete category',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (error) {
                console.error('Error deleting category:', error);
            } finally {
                setLoading(false);
            }
        }
    }, [loadCategories, pagination.page]);

    // Get category icon component
    const getCategoryIcon = useCallback((iconName, color) => {
        const icons = {
            'Folder': <Folder size={16} />,
            'Briefcase': <Briefcase size={16} />,
            'Home': <Home size={16} />,
            'Zap': <Zap size={16} />,
            'Users': <Users size={16} />,
            'MapPin': <MapPin size={16} />,
            'TrendingUp': <TrendingUp size={16} />,
            'Tool': <Tool size={16} />
        };
        return (
            <span style={{ color: color }}>
                {icons[iconName] || <Circle size={16} />}
            </span>
        );
    }, []);

    // Category options for filter dropdown
    // const categoryOptions = useMemo(() => {
    //     return [
    //         { value: '', label: 'All Categories' },
    //         ...(categories || []).map(cat => ({
    //             value: cat.id,
    //             label: cat.category_name
    //         }))
    //     ];
    // }, [categories]);

    // Table columns
    const columns = useMemo(() => [
        {
            title: "Category",
            dataIndex: "category_name",
            sorter: true,
            render: (text, record) => (
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: (record.color || '#6c757d') + '20', width: '32px', height: '32px' }}
                    >
                        {getCategoryIcon(record.icon, record.color)}
                    </div>
                    <div>
                        <strong>{text}</strong>
                        {record.is_default === 1 && (
                            <span className="badge bg-secondary ms-2">Default</span>
                        )}
                        <br />
                        <small className="text-muted">{record.category_slug}</small>
                    </div>
                </div>
            ),
        },
        {
            title: "Description",
            dataIndex: "description",
            render: (text) => text || '-',
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span className={`badge ${expenseCategoryActions.getStatusBadge(text)}`}>
                    {text?.toUpperCase()}
                </span>
            ),
        },
        {
            title: "Created",
            dataIndex: "created_at",
            render: (text) => expenseCategoryActions.formatDate(text),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (text, record) => (
                <div className="btn-group btn-group-sm" role="group">
                    <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                        <button
                            className="btn btn-outline-info"
                            onClick={() => handleView(record)}
                        >
                            <Eye size={14} />
                        </button>
                    </OverlayTrigger>

                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit Category</Tooltip>}>
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(record)}
                            disabled={record.is_default === 1}
                        >
                            <Edit size={14} />
                        </button>
                    </OverlayTrigger>

                    <OverlayTrigger placement="top" overlay={<Tooltip>Delete Category</Tooltip>}>
                        <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(record)}
                            disabled={record.is_default === 1}
                        >
                            <Trash2 size={14} />
                        </button>
                    </OverlayTrigger>
                </div>
            ),
        },
    ], [getCategoryIcon, handleView, handleEdit, handleDelete]);

    // Render filter bar
    const renderFilterBar = useCallback(() => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by category name or description..."
                                    value={filters.search}
                                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                                    Search
                                </button>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <Select
                                options={statusOptions}
                                value={statusOptions.find(opt => opt.value === filters.status)}
                                onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                placeholder="Status"
                                isClearable
                                isDisabled={loading}
                            />
                        </div>
                        <div className="col-md-3">
                            <div className="d-flex gap-2">
                                <Select
                                    className="flex-grow-1"
                                    options={sortOptions}
                                    value={sortOptions.find(opt => opt.value === sortOption)}
                                    onChange={handleSortChange}
                                    placeholder="Sort by"
                                    isClearable={false}
                                    isDisabled={loading}
                                />
                                <button className="btn btn-outline-secondary" onClick={resetFilters} disabled={loading}>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [filters, loading, handleSearch, handleFilterChange, handleSortChange, resetFilters, sortOption, statusOptions, sortOptions]);

    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    if (initialLoad && loading) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Categories...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Expense Categories</h4>
                            <h6>Manage expense categories</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAdd}
                                disabled={loading}
                            >
                                <PlusCircle size={18} className="me-2" />
                                Add Category
                            </button>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => loadCategories(pagination.page)}
                                    disabled={loading}
                                >
                                    <RefreshCw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Filter Bar */}
                {renderFilterBar()}

                {/* Categories Table */}
                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <Table
                                columns={columns}
                                dataSource={categories}
                                loading={loading}
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total,
                                    onChange: handlePageChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} categories`
                                }}
                                rowKey="id"
                            />
                        </div>

                        {!loading && categories.length === 0 && !initialLoad && (
                            <div className="text-center py-4">
                                <AlertCircle size={48} className="text-muted mb-3" />
                                <p className="text-muted">No categories found</p>
                                <button className="btn btn-primary btn-sm" onClick={handleAdd}>
                                    Add your first category
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {modalType === 'add' && (
                <AddExpenseCategory
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    isOpen={true}
                />
            )}

            {modalType === 'edit' && selectedCategory && (
                <EditExpenseCategory
                    categoryId={selectedCategory.id}
                    category={selectedCategory}
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    isOpen={true}
                />
            )}

            {modalType === 'view' && selectedCategory && (
                <ViewExpenseCategory
                    category={selectedCategory}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default ExpenseCategoryList;